import React, { Component } from 'react';
import moment from 'moment';
import { pathOr, without } from 'ramda';
import { FEE_MODES, FORM_MODES } from './constants';
import FeeList from './FeeList';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import {
  getAllPricings,
  createPricing,
  updatePricing,
  deletePricing,
} from './actions';
import { getAllUtilityAllowanceTypes } from '../../App/actions';
import { getUtilityAllowanceTypeOptions } from '../../App/selectors';

import componentMessages from './messages';
import feeListMessages from './FeeList/messages';
import generalMessages from '../../App/messages';
import { ProgramHelper } from '../../GeneralAffordable/programHelper';
const messages = { ...generalMessages, ...componentMessages };

type FormMode = $Values<typeof FORM_MODES>;
type FeeMode = $Values<typeof FEE_MODES>;

type ConnectedProps = {
  currentMarketRent: string,
  currentQuotingRent: string,
  currentDeposit: string,
  currentFinancialMarketRent: string,
  marketRents: Array<Object>,
  quotingRents: Array<Object>,
  financialMarketRents: Array<Object>,
  deposits: Array<Object>,
  allowances: Array<Object>,
  allowanceValues: Array<Object>,
  isAffordable: boolean,
  isAffordableMixedProperty: boolean,
  propertyHadAffordableProgram: boolean,
  HUDGrossRent: string,
  HUDGrossRents: Array<Object>,
  affordableProgramGrossRents: Object,
  isHUDFloorPlan: boolean,
  isRDFloorPlan: boolean,
  isLIHTCFloorPlan: boolean,
  rdRents: Object,
  basicRents: Object,
  noteRent: string,
  basicRent: string,
  noteRents: Array<Object>,
  hud236BasicRents: Array<Object>,
  hud236MarketRents: Array<Object>,
  utilityAllowanceTypes: Array<Object>,
  locale: string,
  currentNonOptionalCharge: string,
  nonOptionalCharges: Array<Object>,
  isAffordableMixedLIHTC: boolean,
  intl: Object,
  isSection236: boolean,
  flags: Object,
  affordablePrograms: Array<string>,
  // Unfortunately the props need a key for each program, so as we add more programs
  // to the Gross Rent by Program feature; we will need to add more keys here.
  HOMEGrossRent: string,
  HTFGrossRent: string,
  phFlatRents: Array<Object>,
  currentPHFlatRent: number,
  phMaximumRents: Array<Object>,
  currentPHMaximumRent: number,
};

type ConnectedMethods = {
  actions: {
    getAllPricings: Function,
    createPricing: Function,
    updatePricing: Function,
    deletePricing: Function,
    getAllUtilityAllowanceTypes: Function,
  },
};

type Props = ConnectedProps &
  ConnectedMethods & {
    id: string,
  };

type State = {
  feeMode: ?FeeMode,
  formMode: ?FormMode,
};

function getCurrentValue(feesList) {
  let currentValue = 0;
  const today = moment().unix();
  feesList.forEach((val) => {
    const start = moment(val.startDate).startOf('day').unix();
    const end =
      val.endDate !== null ? moment(val.endDate).endOf('day').unix() : null;
    if (today > start && (end === null || today < end)) {
      currentValue = val.feeAmount;
    }
  });

  return currentValue;
}

export class EditFees extends Component<Props, State> {
  constructor() {
    super();
    this.state = {
      feeMode: null,
      formMode: null,
    };
  }

  componentDidMount() {
    const {
      id,
      actions: { getAllPricings, getAllUtilityAllowanceTypes },
    } = this.props;
    getAllPricings(id);
    getAllUtilityAllowanceTypes();
  }

  resetFeeMode = (feeMode: FeeMode) => {
    if (feeMode === this.state.feeMode) {
      this.setState({ feeMode: null });
    }
  };

  setFeeMode = (feeMode: FeeMode) => {
    if (this.state.feeMode === null) this.setState({ feeMode });
  };

  handleCollapse = (type: FeeMode) => {
    this.resetFeeMode(type);
  };

  handleExpand = (type: FeeMode) => {
    this.setFeeMode(type);
  };

  handleCreate = (pricing: Object) => {
    const {
      actions: { createPricing },
    } = this.props;
    createPricing(pricing);
  };

  handleUpdate = (pricing: Object) => {
    const {
      actions: { updatePricing },
    } = this.props;
    updatePricing(pricing);
  };

  handleDelete = (pricing: Object) => {
    const {
      actions: { deletePricing },
    } = this.props;
    deletePricing(pricing);
  };

  render() {
    const {
      marketRents,
      quotingRents,
      deposits,
      financialMarketRents,
      currentMarketRent,
      currentQuotingRent,
      currentFinancialMarketRent,
      currentDeposit,
      allowances,
      allowanceValues,
      isAffordable,
      isAffordableMixedProperty,
      propertyHadAffordableProgram,
      HUDGrossRent,
      HUDGrossRents,
      affordableProgramGrossRents,
      hud236MarketRent,
      hud236MarketRents,
      noteRent,
      basicRent,
      basicRents,
      noteRents,
      hud236BasicRent,
      hud236BasicRents,
      isHUDFloorPlan,
      isRDFloorPlan,
      isLIHTCFloorPlan,
      rdRents,
      flags,
      locale,
      utilityAllowanceTypes,
      nonOptionalCharges,
      currentNonOptionalCharge,
      phFlatRents,
      currentPHFlatRent,
      phMaximumRents,
      currentPHMaximumRent,
      isAffordableMixedLIHTC,
      intl,
      isSection236,
      affordablePrograms,
    } = this.props;
    const { feeMode } = this.state;
    const {
      MARKET_RENT,
      QUOTING_RENT,
      DEPOSIT,
      UTILITY_ALLOWANCE,
      HUD_GROSS_RENT,
      NOTE_RENT,
      BASIC_RENT,
      FINANCIAL_MARKET_RENT,
      NON_OPTIONAL_CHARGE,
      HOME_GROSS_RENT,
      HTF_GROSS_RENT,
    } = FEE_MODES;
    const HUD_236_BASIC_RENT = FEE_MODES['236_BASIC_RENT'];
    const HUD_236_MARKET_RENT = FEE_MODES['236_MARKET_RENT'];
    const nonOptionalChargessFeature =
      flags?.nonOptionalChargessFeature ?? false;
    const updatedHudGrcLogic = flags?.updatedHudGrcLogic ?? false;
    const hudPricingViewOnly = updatedHudGrcLogic && isHUDFloorPlan;
    const hudGrossRentFeeListViewProps = hudPricingViewOnly
      ? {
          viewOnly: true,
          expandedPanelTitle: intl.formatMessage(
            feeListMessages.hudGrossRentLimitHistory,
          ),
        }
      : {};
    const program = new ProgramHelper(affordablePrograms);

    const isRDOnly = isRDFloorPlan && !isLIHTCFloorPlan && !isHUDFloorPlan;

    const allowancesSet = new Set();
    // Use a set to remove duplicate allowances
    allowanceValues.map((allowance) =>
      allowancesSet.add(JSON.stringify(allowance?.allowances)),
    );
    const allowancesArray = Array.from(allowancesSet).map((allowance) =>
      JSON.parse(allowance),
    );

    const grossRentLimitLabel = isAffordable
      ? intl.formatMessage(messages.grossRentLimitCustom, {
          programName: '',
        })
      : intl.formatMessage(messages.currentMarketRent);

    const hasHOME = affordablePrograms?.includes('HOME');
    const hasHTF = affordablePrograms?.includes('HTF');

    const feeListCommonProps = {
      onCollapse: this.handleCollapse,
      onExpand: this.handleExpand,
      onUpdate: this.handleUpdate,
      onCreate: this.handleCreate,
      onDelete: this.handleDelete,
      flags,
      utilityAllowanceTypes,
      locale,
      intl,
    };
    return (
      <div className="accordion panel-group">
        <FeeList
          title={grossRentLimitLabel}
          collapsed={MARKET_RENT !== feeMode}
          disabled={feeMode !== null && MARKET_RENT !== feeMode}
          type={MARKET_RENT}
          fees={marketRents}
          currentRate={currentMarketRent}
          {...feeListCommonProps}
        />
        {hasHOME && (
          <FeeList
            title={intl.formatMessage(messages.grossRentLimitCustom, {
              programName: 'HOME ',
            })}
            collapsed={HOME_GROSS_RENT !== feeMode}
            disabled={feeMode !== null && HOME_GROSS_RENT !== feeMode}
            type={HOME_GROSS_RENT}
            fees={affordableProgramGrossRents['HOME'] ?? []}
            currentRate={this.props.HOMEGrossRent}
            {...feeListCommonProps}
          />
        )}
        {hasHTF && (
          <FeeList
            title={intl.formatMessage(messages.grossRentLimitCustom, {
              programName: 'HTF ',
            })}
            collapsed={HTF_GROSS_RENT !== feeMode}
            disabled={feeMode !== null && HTF_GROSS_RENT !== feeMode}
            type={HTF_GROSS_RENT}
            fees={affordableProgramGrossRents['HTF'] ?? []}
            currentRate={this.props.HTFGrossRent}
            {...feeListCommonProps}
          />
        )}
        {isHUDFloorPlan && (
          <FeeList
            title={intl.formatMessage(messages.hudGrossRentLimit)}
            collapsed={HUD_GROSS_RENT !== feeMode}
            disabled={feeMode !== null && HUD_GROSS_RENT !== feeMode}
            type={HUD_GROSS_RENT}
            fees={HUDGrossRents}
            currentRate={HUDGrossRent}
            {...feeListCommonProps}
            {...hudGrossRentFeeListViewProps}
          />
        )}
        {isHUDFloorPlan && isSection236 && (
          <FeeList
            title={intl.formatMessage(messages.hud236BasicRent)}
            collapsed={HUD_236_BASIC_RENT !== feeMode}
            disabled={feeMode !== null && HUD_236_BASIC_RENT !== feeMode}
            type={HUD_236_BASIC_RENT}
            fees={hud236BasicRents}
            currentRate={hud236BasicRent}
            {...feeListCommonProps}
          />
        )}
        {isHUDFloorPlan && isSection236 && (
          <FeeList
            title={intl.formatMessage(messages.hud236MarketRent)}
            collapsed={HUD_236_MARKET_RENT !== feeMode}
            disabled={feeMode !== null && HUD_236_MARKET_RENT !== feeMode}
            type={HUD_236_MARKET_RENT}
            fees={hud236MarketRents}
            currentRate={hud236MarketRent}
            {...feeListCommonProps}
          />
        )}
        {isRDFloorPlan && rdRents.noteRent && (
          <FeeList
            title={intl.formatMessage(messages.noteRent)}
            collapsed={NOTE_RENT !== feeMode}
            disabled={feeMode !== null && NOTE_RENT !== feeMode}
            type={NOTE_RENT}
            isRDOnly={isRDOnly}
            fees={noteRents}
            currentRate={noteRent}
            {...feeListCommonProps}
          />
        )}
        {isRDFloorPlan && rdRents.basicRent && (
          <FeeList
            title={intl.formatMessage(messages.basicRent)}
            collapsed={BASIC_RENT !== feeMode}
            disabled={feeMode !== null && BASIC_RENT !== feeMode}
            type={BASIC_RENT}
            fees={basicRents}
            currentRate={basicRent}
            {...feeListCommonProps}
          />
        )}
        <FeeList
          title={intl.formatMessage(messages.currentQuotingRent)}
          collapsed={QUOTING_RENT !== feeMode}
          disabled={feeMode !== null && QUOTING_RENT !== feeMode}
          type={QUOTING_RENT}
          fees={quotingRents}
          currentRate={currentQuotingRent}
          {...feeListCommonProps}
        />
        <FeeList
          title={intl.formatMessage(messages.currentSuggestedDeposit)}
          collapsed={DEPOSIT !== feeMode}
          disabled={feeMode !== null && DEPOSIT !== feeMode}
          type={DEPOSIT}
          fees={deposits}
          currentRate={currentDeposit}
          {...feeListCommonProps}
          isCurrentSuggestedDeposit={true}
        />
        {(isAffordableMixedProperty || propertyHadAffordableProgram) && (
          <FeeList
            title={intl.formatMessage(messages.financialMarketRent)}
            collapsed={FINANCIAL_MARKET_RENT !== feeMode}
            disabled={feeMode !== null && FINANCIAL_MARKET_RENT !== feeMode}
            type={FINANCIAL_MARKET_RENT}
            fees={financialMarketRents}
            currentRate={currentFinancialMarketRent}
            {...feeListCommonProps}
          />
        )}
        {isAffordableMixedLIHTC && nonOptionalChargessFeature && (
          <FeeList
            title={intl.formatMessage(messages.nonOptionalCharges)}
            collapsed={NON_OPTIONAL_CHARGE !== feeMode}
            disabled={feeMode !== null && NON_OPTIONAL_CHARGE !== feeMode}
            type={NON_OPTIONAL_CHARGE}
            fees={nonOptionalCharges}
            isRDOnly={isRDOnly}
            currentRate={currentNonOptionalCharge}
            {...feeListCommonProps}
          />
        )}
        {program.isPH && (
          <>
            <FeeList
              title={intl.formatMessage(messages.phFlatRent)}
              collapsed={FEE_MODES.PH_FLAT_RENT !== feeMode}
              disabled={feeMode !== null && FEE_MODES.PH_FLAT_RENT !== feeMode}
              type={FEE_MODES.PH_FLAT_RENT}
              fees={phFlatRents}
              currentRate={currentPHFlatRent}
              // for this component it must be set the day after
              today={moment.utc().startOf('date').add(1, 'days')}
              {...feeListCommonProps}
            />
            <FeeList
              title={intl.formatMessage(messages.phMaximumRent)}
              collapsed={FEE_MODES.PH_MAXIMUM_RENT !== feeMode}
              disabled={
                feeMode !== null && FEE_MODES.PH_MAXIMUM_RENT !== feeMode
              }
              type={FEE_MODES.PH_MAXIMUM_RENT}
              fees={phMaximumRents}
              currentRate={currentPHMaximumRent}
              // for this component it must be set the day after
              today={moment.utc().startOf('date').add(1, 'days')}
              {...feeListCommonProps}
            />
          </>
        )}
        {allowances && isAffordable
          ? allowancesArray.map((allowance, key) => {
              const name = pathOr('', ['pha', 'name'], allowance);
              const allowanceType = `${name}_${UTILITY_ALLOWANCE}`;
              const fees = allowanceValues
                .filter(
                  (allowance) =>
                    name ===
                    pathOr('', ['allowances', 'pha', 'name'], allowance),
                )
                .reduce(
                  (prev, val) => {
                    const valType = pathOr(
                      '',
                      ['allowances', 'pha', 'name'],
                      val,
                    );
                    val.uaTypes = without(
                      [null, undefined],
                      (val?.fpuaTypes ?? []).map(
                        (fpuaType) => fpuaType?.utilityAllowanceType,
                      ),
                    );
                    if (valType === name) prev.list.push(val);
                    const today = moment().unix();
                    const start = moment(val.startDate).startOf('day').unix();
                    const end =
                      val.endDate !== null
                        ? moment(val.endDate).endOf('day').unix()
                        : null;
                    if (today > start && (end === null || today < end)) {
                      prev.currentRate = val.feeAmount;
                    }
                    return prev;
                  },
                  { list: [], currentRate: allowance.utilityAllowanceAmount },
                );
              const utilityAlowancesId = pathOr('', ['id'], allowance);
              const isHudAllowance = allowance?.pha?.isHUD === true;
              const isHudAllowanceViewOnly =
                hudPricingViewOnly && isHudAllowance;
              const uaMessage = isHudAllowanceViewOnly
                ? intl.formatMessage(
                    feeListMessages.hudRentScheduleUtilityAllowance,
                  )
                : `${name} ${intl.formatMessage(messages.utilityAllowance)}`;
              const hudAllowanceFeeListViewProps = isHudAllowanceViewOnly
                ? {
                    viewOnly: true,
                    expandedPanelTitle: intl.formatMessage(
                      feeListMessages.hudRentScheduleUtilityAllowanceHistory,
                    ),
                  }
                : {};
              return (
                <FeeList
                  key={key}
                  title={uaMessage}
                  collapsed={allowanceType !== feeMode}
                  disabled={feeMode !== null && allowanceType !== feeMode}
                  type={allowanceType}
                  fees={fees.list}
                  currentRate={fees.currentRate}
                  {...feeListCommonProps}
                  {...hudAllowanceFeeListViewProps}
                  onCreate={(pricing) =>
                    this.handleCreate({ ...pricing, utilityAlowancesId })
                  }
                />
              );
            })
          : null}
      </div>
    );
  }
}

export function mapStateToProps(state: Object): Object {
  const phMaximumRents = state?.viewUnitEditFees?.phMaximumRents ?? [];
  const currentPHMaximumRent = getCurrentValue(phMaximumRents);
  const phFlatRents = state?.viewUnitEditFees?.phFlatRents ?? [];
  const currentPHFlatRent = getCurrentValue(phFlatRents);
  return {
    currentMarketRent: state?.viewUnitEditFees?.currentMarketRent ?? '0',
    marketRents: state?.viewUnitEditFees?.marketRents ?? [],
    currentQuotingRent: state?.viewUnitEditFees?.currentQuotingRent ?? '0',
    quotingRents: state?.viewUnitEditFees?.quotingRents ?? [],
    currentFinancialMarketRent:
      state?.viewUnitEditFees?.currentFinancialMarketRent ?? '0',
    propertyHadAffordableProgram:
      state?.viewUnitEditFees?.propertyHadAffordableProgram ?? false,
    HUDGrossRent: state?.viewUnitEditFees?.HUDGrossRent ?? '0',
    HUDGrossRents: state?.viewUnitEditFees?.HUDGrossRents ?? [],
    hud236MarketRents: state?.viewUnitEditFees?.hud236MarketRents ?? [],
    hud236MarketRent: state?.viewUnitEditFees?.hud236MarketRent ?? '0',
    noteRent: state?.viewUnitEditFees?.noteRent ?? '0',
    basicRent: state?.viewUnitEditFees?.basicRent ?? '0',
    noteRents: state?.viewUnitEditFees?.noteRents ?? [],
    basicRents: state?.viewUnitEditFees?.basicRents ?? [],
    hud236BasicRents: state?.viewUnitEditFees?.hud236BasicRents ?? [],
    hud236BasicRent: state?.viewUnitEditFees?.hud236BasicRent ?? '0',
    currentDeposit: state?.viewUnitEditFees?.currentDeposit ?? '0',
    deposits: state?.viewUnitEditFees?.deposits ?? [],
    currentNonOptionalCharge:
      state?.viewUnitEditFees?.currentNonOptionalCharge ?? '0',
    nonOptionalCharges: state?.viewUnitEditFees?.nonOptionalCharges ?? [],
    allowanceValues: state?.viewUnitEditFees?.allowances ?? [],
    utilityAllowanceTypes: getUtilityAllowanceTypeOptions(state),
    locale: state?.languageProvider?.locale,
    financialMarketRents: state?.viewUnitEditFees?.financialMarketRents ?? [],
    affordableProgramGrossRents:
      state?.viewUnitEditFees?.affordableProgramGrossRents ?? {},
    // Unfortunately the props need a key for each program, so as we add more programs
    // to the Gross Rent by Program feature; we will need to add more keys here.
    HOMEGrossRent: state?.viewUnitEditFees?.HOMEGrossRent ?? '0',
    HTFGrossRent: state?.viewUnitEditFees?.HTFGrossRent ?? '0',
    phFlatRents,
    currentPHFlatRent,
    phMaximumRents,
    currentPHMaximumRent,
  };
}

export function mapDispatchToProps(dispatch: Object): ConnectedMethods {
  return {
    actions: bindActionCreators(
      {
        getAllPricings,
        createPricing,
        updatePricing,
        deletePricing,
        getAllUtilityAllowanceTypes,
      },
      dispatch,
    ),
  };
}

const connected = connect(mapStateToProps, mapDispatchToProps)(EditFees);

export default withLDConsumer()(connected);
